import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { IMG } from "@/lib/content";
import ConceptTag from "@/components/site/ConceptTag";

const scrollTo = (id) => () => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function FinalCta() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section ref={ref} className="relative overflow-hidden" data-testid="final-cta">
      <motion.div style={{ y }} className="absolute inset-0 scale-110">
        <img src={IMG.exterior} alt="RestPower destination concept" loading="lazy" className="h-full w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-black/70" />
      <div className="absolute inset-0 bg-gradient-to-t from-rp-bg via-rp-bg/40 to-rp-bg/60" />
      <ConceptTag className="absolute right-6 top-6 md:right-12" />

      <div className="relative mx-auto max-w-7xl px-6 py-28 md:px-12 md:py-40">
        <motion.h2
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl font-display text-4xl font-light uppercase leading-[0.95] tracking-tight text-white md:text-6xl lg:text-7xl"
        >
          The road is changing.<br /><span className="text-rp-accent">The stop should too.</span>
        </motion.h2>
        <p className="mt-7 max-w-xl text-base leading-relaxed text-white/70 md:text-lg">
          RestPower is building a new vision for the roadside experience — where charging your vehicle is
          only the beginning.
        </p>
        <div className="mt-10 flex flex-col gap-4 sm:flex-row">
          <button onClick={scrollTo("partners")} data-testid="final-primary-cta"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-rp-accent px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.14em] text-black transition-colors duration-200 hover:bg-white">
            Explore Partnership Opportunities
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </button>
          <Link to="/journal" data-testid="final-secondary-cta"
            className="group inline-flex items-center justify-center gap-2 rounded-full border border-white/25 px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.14em] text-white transition-colors duration-200 hover:border-rp-accent hover:text-rp-accent">
            Follow the RestPower Journey
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
}
